import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import MediaLightbox from "@/components/MediaLightbox";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/auth";
import { Star, ShieldCheck, MapPin, Path, ArrowLeft, Heart } from "@phosphor-icons/react";

export default function PublicRiderProfile() {
  const { rider_id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/r/${rider_id}`).then(({ data }) => setData(data)).catch(() => setErr("Rider not found"));
  }, [rider_id]);

  if (err) return <div className="p-10 text-center text-foreground/60">{err}</div>;
  if (!data) return <div className="p-10 text-center text-foreground/60">Loading…</div>;

  const initial = (data.name || "R").trim().charAt(0).toUpperCase();
  const ratingStars = Math.round(data.rider_rating_avg || 0);

  return (
    <div className="min-h-screen bg-background">
      {user && <Navbar />}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/" className="font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1 mb-4 hover:underline">
          <ArrowLeft size={12} /> Home
        </Link>

        <section className="border-2 border-foreground bg-card p-5 shadow-[4px_4px_0_0_#FFB800]" data-testid="rider-profile-header">
          <div className="flex items-center gap-4">
            {data.picture ? (
              <img src={data.picture} alt="" referrerPolicy="no-referrer" className="w-20 h-20 object-cover border-2 border-foreground" />
            ) : (
              <div className="w-20 h-20 bg-foreground text-background border-2 border-foreground inline-flex items-center justify-center font-display text-3xl">{initial}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60">Rider</div>
              <h1 className="font-display text-3xl tracking-tight flex items-center gap-2">
                {data.name}
                {data.verified && <ShieldCheck size={18} weight="fill" className="text-accent" />}
              </h1>
              <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 mt-1">
                Member since {(data.joined_at || "").slice(0, 10)}
              </div>
            </div>
          </div>

          {data.bio && <p className="mt-4 text-sm text-foreground/80">{data.bio}</p>}

          <div className="grid grid-cols-3 gap-2 mt-5">
            <Stat label="Trips" value={data.total_trips} />
            <Stat label="Rating" value={data.rider_rating_avg ? data.rider_rating_avg.toFixed(1) : "—"} sub={
              <span className="inline-flex">{[1,2,3,4,5].map(i => <Star key={i} size={10} weight={i <= ratingStars ? "fill" : "regular"} className="text-primary" />)}</span>
            } />
            <Stat label="Reviews" value={data.rider_rating_count} />
          </div>

          {data.accessibility && (
            <div className="mt-3 text-xs flex items-start gap-2 border-l-2 border-primary pl-2 py-1 bg-primary/10">
              <strong className="font-mono text-[10px] uppercase tracking-wider">Accessibility:</strong>
              <span>{data.accessibility}</span>
            </div>
          )}
        </section>

        <section className="mt-6">
          <h2 className="font-display text-2xl mb-3">Trip timeline · {data.trips.length}</h2>
          {data.trips.length === 0 ? (
            <div className="border-2 border-dashed border-foreground/30 p-6 text-center text-foreground/60 text-sm">
              No trips yet — this rider hasn't taken a ride on RideGY.
            </div>
          ) : (
            <ol className="space-y-2" data-testid="rider-trip-list">
              {data.trips.map((t) => (
                <li key={t.ride_id} className="border-2 border-foreground bg-card p-3" data-testid={`rider-trip-${t.ride_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                      {(t.completed_at || "").slice(0, 10)}
                    </span>
                    <span className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${t.status === "completed" ? "bg-accent text-white" : "bg-foreground/10"}`}>
                      {t.status}
                    </span>
                  </div>
                  <div className="mt-1 text-sm flex items-center gap-2 flex-wrap">
                    <MapPin size={12} weight="fill" className="text-primary shrink-0" />
                    <span className="font-medium">{t.pickup}</span>
                    <Path size={12} className="text-foreground/60" />
                    <span className="font-medium">{t.dropoff}</span>
                  </div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-foreground/60 flex items-center gap-2 flex-wrap">
                    <span>{t.distance_km} km</span>
                    {t.fare_gyd && <span>· G${t.fare_gyd.toLocaleString()}</span>}
                    {t.driver_name && t.driver_id && (
                      <span>· with <Link className="underline" to={`/d/${t.driver_id}`}>{t.driver_name}</Link></span>
                    )}
                    {t.rider_rating ? (
                      <span className="inline-flex items-center gap-1 text-primary">
                        · {t.rider_rating} <Star size={10} weight="fill" />
                      </span>
                    ) : t.status === "completed" ? (
                      <span>· not rated</span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <RiderGallerySection rider_id={rider_id} />
      </div>
    </div>
  );
}


function RiderGallerySection({ rider_id }) {
  const { user } = useAuth();
  const [media, setMedia] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  useEffect(() => {
    api.get(`/public/rider/${rider_id}/media`)
      .then(({ data }) => setMedia(data || []))
      .catch(() => setMedia([]));
  }, [rider_id]);
  if (!media || media.length === 0) return null;
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const mediaUrl = (p) => `${API_URL}/api/public/media/${p}`;

  const toggleLike = async (file_id) => {
    if (!user) {
      window.location.href = "/";
      return;
    }
    // Optimistic update
    setMedia((prev) => prev.map((m) => m.file_id === file_id
      ? { ...m, viewer_liked: !m.viewer_liked, like_count: (m.like_count || 0) + (m.viewer_liked ? -1 : 1) }
      : m));
    try {
      const { data } = await api.post(`/users/media/${file_id}/like`);
      setMedia((prev) => prev.map((m) => m.file_id === file_id
        ? { ...m, viewer_liked: data.liked, like_count: data.like_count }
        : m));
    } catch {
      // Rollback on failure
      setMedia((prev) => prev.map((m) => m.file_id === file_id
        ? { ...m, viewer_liked: !m.viewer_liked, like_count: (m.like_count || 0) + (m.viewer_liked ? -1 : 1) }
        : m));
    }
  };
  return (
    <section className="mt-6 border-2 border-foreground bg-card p-5" data-testid="rider-profile-gallery">
      <h2 className="font-display text-2xl mb-3">Gallery</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {media.map((m, idx) => (
          <div key={m.file_id} className="space-y-1">
          <button
            type="button"
            onClick={() => setLightboxIndex(idx)}
            className="relative border-2 border-foreground bg-foreground aspect-square overflow-hidden text-left cursor-pointer group w-full"
            data-testid={`rider-profile-media-${m.file_id}`}
          >
            {m.kind === "rider_media_video" ? (
              <>
                {m.poster_path ? (
                  <img
                    src={mediaUrl(m.poster_path)}
                    alt=""
                    className="w-full h-full object-cover pointer-events-none"
                  />
                ) : (
                  <video
                    src={`${mediaUrl(m.storage_path)}#t=0.1`}
                    playsInline
                    muted
                    preload="metadata"
                    className="w-full h-full object-cover bg-foreground pointer-events-none"
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid={`rider-profile-video-overlay-${m.file_id}`}>
                  <div className="w-12 h-12 rounded-full bg-foreground/70 border-2 border-background flex items-center justify-center">
                    <div
                      className="w-0 h-0 ml-1"
                      style={{
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderLeft: "12px solid #fff",
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <img src={mediaUrl(m.storage_path)} alt="" className="w-full h-full object-cover" />
            )}
          </button>
          {m.caption && (
            <p
              className="text-xs text-foreground/70 line-clamp-2 px-0.5"
              data-testid={`rider-profile-caption-${m.file_id}`}
            >
              {m.caption}
            </p>
          )}
          <div className="flex items-center gap-1 px-0.5">
            <button
              type="button"
              onClick={() => toggleLike(m.file_id)}
              data-testid={`rider-profile-like-${m.file_id}`}
              className={`inline-flex items-center gap-1 text-xs hover:opacity-80 transition-opacity ${m.viewer_liked ? "text-primary" : "text-foreground/60"}`}
              title={m.viewer_liked ? "Unlike" : "Like"}
              aria-pressed={m.viewer_liked ? "true" : "false"}
            >
              <Heart size={14} weight={m.viewer_liked ? "fill" : "regular"} />
              <span className="font-mono" data-testid={`rider-profile-like-count-${m.file_id}`}>
                {m.like_count || 0}
              </span>
            </button>
          </div>
          </div>
        ))}
      </div>
      <MediaLightbox
        items={media}
        index={lightboxIndex}
        mediaUrl={mediaUrl}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    </section>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border-2 border-foreground p-2 text-center bg-background">
      <div className="font-display text-xl">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-wider text-foreground/60">{label}</div>
      {sub && <div className="mt-1">{sub}</div>}
    </div>
  );
}
